// src/components/admin/StaffPayrollTab.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { getSalonStaff } from '../../api/salonApi';
import '../../styles/staff-payroll.css';

// Format date to "YYYY-MM-DD" for roster key lookup
function formatDateKey(date) {
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Get Mon-Sun week ranges that overlap with a given month (0-indexed month)
function getWeeksInMonth(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const weeks = [];
  let weekNum = 1;

  // Find the Monday of the week containing the 1st
  const dayOfWeek = firstDay.getDay(); // 0=Sun, 1=Mon...
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const weekStart = new Date(firstDay);
  weekStart.setDate(firstDay.getDate() + mondayOffset);

  while (weekStart <= lastDay) {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    // Only include dates within the target month
    const datesInMonth = [];
    for (let d = new Date(weekStart); d <= weekEnd; d.setDate(d.getDate() + 1)) {
      if (d.getMonth() === month && d.getFullYear() === year) {
        datesInMonth.push(new Date(d));
      }
    }

    if (datesInMonth.length > 0) {
      weeks.push({
        weekNum,
        weekStart: new Date(weekStart),
        weekEnd: new Date(weekEnd),
        datesInMonth
      });
      weekNum++;
    }

    weekStart.setDate(weekStart.getDate() + 7);
  }

  return weeks;
}

// Calculate total hours from roster for an array of dates
function calculateHoursForDates(dates, roster) {
  let totalSlots = 0;
  dates.forEach(d => {
    const key = formatDateKey(d);
    const slots = roster[key];
    if (Array.isArray(slots)) {
      totalSlots += slots.length;
    }
  });
  return totalSlots * 0.5;
}

// Calculate pay based on rateType
function calculatePay(totalHours, weeksWorked, rate, rateType) {
  if (!rate || rate <= 0) return 0;
  switch (rateType) {
    case 'monthly':
      return rate;
    case 'weekly':
      return weeksWorked * rate;
    case 'hourly':
      return totalHours * rate;
    default:
      return 0;
  }
}

function StaffPayrollTab({ salonId, onPreview }) {
  const [staffList, setStaffList] = useState([]);
  const [workingHoursMap, setWorkingHoursMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [reportType, setReportType] = useState('summary');

  const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);

  const [reportData, setReportData] = useState(null);

  const staffDropdownRef = useRef(null);
  const monthDropdownRef = useRef(null);

  // Click-outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (staffDropdownRef.current && !staffDropdownRef.current.contains(e.target)) {
        setStaffDropdownOpen(false);
      }
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(e.target)) {
        setMonthDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load data on mount
  useEffect(() => {
    if (!salonId) return;
    loadData();
  }, [salonId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [staffData, whSnapshot] = await Promise.all([
        getSalonStaff(salonId),
        getDocs(query(
          collection(db, 'workingHours'),
          where('salonId', '==', salonId)
        ))
      ]);

      setStaffList(staffData.filter(s => s.isActive !== false));

      const whMap = {};
      whSnapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (data.staffId) {
          whMap[data.staffId] = data;
        }
      });
      setWorkingHoursMap(whMap);
    } catch (error) {
      console.error('Error loading payroll data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate month options: current month + last 12
  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }, []);

  // Multi-select helpers
  const toggleStaffSelection = (staffId) => {
    setSelectedStaffIds(prev =>
      prev.includes(staffId) ? prev.filter(id => id !== staffId) : [...prev, staffId]
    );
  };

  const handleSelectAllStaff = () => {
    if (selectedStaffIds.length === staffList.length) {
      setSelectedStaffIds([]);
    } else {
      setSelectedStaffIds(staffList.map(s => s.staffId || s.id));
    }
  };

  const toggleMonthSelection = (monthValue) => {
    setSelectedMonths(prev =>
      prev.includes(monthValue) ? prev.filter(m => m !== monthValue) : [...prev, monthValue]
    );
  };

  const handleSelectAllMonths = () => {
    if (selectedMonths.length === monthOptions.length) {
      setSelectedMonths([]);
    } else {
      setSelectedMonths(monthOptions.map(o => o.value));
    }
  };

  // Generate report
  const generateReport = () => {
    if (selectedStaffIds.length === 0 || selectedMonths.length === 0) return;
    setGenerating(true);

    const results = [];

    selectedStaffIds.forEach(staffId => {
      const staffMember = staffList.find(s => (s.staffId || s.id) === staffId);
      const whData = workingHoursMap[staffId];
      if (!staffMember) return;

      selectedMonths.forEach(monthStr => {
        const [year, month] = monthStr.split('-').map(Number);
        const monthLabel = new Date(year, month - 1, 1)
          .toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

        const employmentType = whData?.employmentType || 'fulltime';
        const rate = whData?.rate || 0;
        const currency = whData?.currency || 'SGD';
        const rateType = whData?.rateType || 'monthly';
        const roster = whData?.roster || {};
        const isFullTime = employmentType === 'fulltime';

        const weeks = getWeeksInMonth(year, month - 1);

        const weeklyBreakdown = weeks.map(week => {
          if (isFullTime) {
            return { ...week, hours: null };
          }
          const hours = calculateHoursForDates(week.datesInMonth, roster);
          return { ...week, hours };
        });

        const totalMonthHours = isFullTime
          ? null
          : weeklyBreakdown.reduce((sum, w) => sum + (w.hours || 0), 0);

        const weeksWorked = isFullTime
          ? weeks.length
          : weeklyBreakdown.filter(w => w.hours > 0).length;

        const totalPay = isFullTime
          ? (rateType === 'monthly' ? rate : 0)
          : calculatePay(totalMonthHours, weeksWorked, rate, rateType);

        results.push({
          staffId,
          staffName: staffMember.name || whData?.staffName || 'Unknown',
          monthStr,
          monthLabel,
          employmentType,
          rate,
          currency,
          rateType,
          isFullTime,
          weeklyBreakdown,
          totalMonthHours,
          weeksWorked,
          totalPay
        });
      });
    });

    setReportData(results);
    setGenerating(false);
  };

  // CSV export with "|" delimiter
  const handleExportCSV = () => {
    if (!reportData || reportData.length === 0) return;

    const delimiter = '|';
    const csvRows = [];

    if (reportType === 'summary') {
      const maxWeeks = Math.max(...reportData.map(r => r.weeklyBreakdown.length));

      const headers = [
        'Staff Name', 'Month', 'Employment Type',
        ...Array.from({ length: maxWeeks }, (_, i) => `Week ${i + 1} Hours`),
        'Total Hours', 'Rate', 'Rate Type', 'Currency', 'Total Pay'
      ];
      csvRows.push(headers.join(delimiter));

      reportData.forEach(row => {
        const weekHours = Array.from({ length: maxWeeks }, (_, i) =>
          row.isFullTime ? 'N/A' : (row.weeklyBreakdown[i]?.hours ?? '')
        );
        const dataRow = [
          row.staffName,
          row.monthLabel,
          row.employmentType,
          ...weekHours,
          row.isFullTime ? 'N/A' : row.totalMonthHours,
          row.rate || '',
          row.rateType,
          row.currency,
          row.totalPay.toFixed(2)
        ];
        csvRows.push(dataRow.join(delimiter));
      });
    } else {
      const headers = [
        'Staff Name', 'Month', 'Employment Type', 'Date', 'Day',
        'Hours Worked', 'Time Slots', 'Rate', 'Rate Type', 'Currency', 'Total Pay'
      ];
      csvRows.push(headers.join(delimiter));

      reportData.forEach(row => {
        if (row.isFullTime) {
          csvRows.push([
            row.staffName, row.monthLabel, row.employmentType,
            '', '', 'Full-time - No roster data', '',
            row.rate || '', row.rateType, row.currency, row.totalPay.toFixed(2)
          ].join(delimiter));
          return;
        }

        const [year, month] = row.monthStr.split('-').map(Number);
        const roster = workingHoursMap[row.staffId]?.roster || {};
        const daysInMonth = new Date(year, month, 0).getDate();
        let hasData = false;

        for (let d = 1; d <= daysInMonth; d++) {
          const date = new Date(year, month - 1, d);
          const dateKey = formatDateKey(date);
          const slots = roster[dateKey] || [];
          if (slots.length === 0) continue;
          hasData = true;
          const hours = slots.length * 0.5;
          const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
          const dateLabel = date.toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric'
          });
          csvRows.push([
            row.staffName, row.monthLabel, row.employmentType,
            dateLabel, dayName, hours, slots.join('; '),
            row.rate || '', row.rateType, row.currency, row.totalPay.toFixed(2)
          ].join(delimiter));
        }

        if (!hasData) {
          csvRows.push([
            row.staffName, row.monthLabel, row.employmentType,
            '', '', 'No roster data', '',
            row.rate || '', row.rateType, row.currency, row.totalPay.toFixed(2)
          ].join(delimiter));
        }
      });
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `staff-payroll-${reportType}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render summary report table
  const renderSummaryReport = () => {
    if (!reportData || reportData.length === 0) {
      return <p className="sp-empty">No data to display.</p>;
    }

    const maxWeeks = Math.max(...reportData.map(r => r.weeklyBreakdown.length));

    return (
      <div className="sp-report-container">
        <table className="sp-report-table">
          <thead>
            <tr>
              <th>Staff Name</th>
              <th>Month</th>
              <th>Employment Type</th>
              {Array.from({ length: maxWeeks }, (_, i) => (
                <th key={i}>Week {i + 1} Hrs</th>
              ))}
              <th>Total Hours</th>
              <th>Rate</th>
              <th>Rate Type</th>
              <th>Currency</th>
              <th>Total Pay</th>
            </tr>
          </thead>
          <tbody>
            {reportData.map((row, idx) => (
              <tr key={idx} className={row.isFullTime ? 'sp-fulltime-row' : ''}>
                <td>{row.staffName}</td>
                <td>{row.monthLabel}</td>
                <td className="sp-capitalize">{row.employmentType}</td>
                {Array.from({ length: maxWeeks }, (_, i) => (
                  <td key={i}>
                    {row.isFullTime
                      ? 'N/A'
                      : (row.weeklyBreakdown[i]?.hours != null ? row.weeklyBreakdown[i].hours : '-')}
                  </td>
                ))}
                <td>{row.isFullTime ? 'N/A' : row.totalMonthHours}</td>
                <td>{row.rate || '-'}</td>
                <td className="sp-capitalize">{row.rateType}</td>
                <td>{row.currency}</td>
                <td className="sp-pay-cell">{row.currency} {row.totalPay.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render detailed breakdown table
  const renderDetailedReport = () => {
    if (!reportData || reportData.length === 0) {
      return <p className="sp-empty">No data to display.</p>;
    }

    return (
      <div className="sp-report-container">
        <table className="sp-report-table">
          <thead>
            <tr>
              <th>Staff Name</th>
              <th>Month</th>
              <th>Employment Type</th>
              <th>Date</th>
              <th>Day</th>
              <th>Hours Worked</th>
              <th>Time Slots</th>
            </tr>
          </thead>
          <tbody>
            {reportData.map((row, rowIdx) => {
              if (row.isFullTime) {
                return (
                  <tr key={rowIdx} className="sp-fulltime-row">
                    <td>{row.staffName}</td>
                    <td>{row.monthLabel}</td>
                    <td className="sp-capitalize">{row.employmentType}</td>
                    <td colSpan="4" className="sp-fulltime-message">
                      Full-time - No roster data
                    </td>
                  </tr>
                );
              }

              const [year, month] = row.monthStr.split('-').map(Number);
              const roster = workingHoursMap[row.staffId]?.roster || {};
              const daysInMonth = new Date(year, month, 0).getDate();
              const dayRows = [];

              for (let d = 1; d <= daysInMonth; d++) {
                const date = new Date(year, month - 1, d);
                const dateKey = formatDateKey(date);
                const slots = roster[dateKey] || [];
                if (slots.length === 0) continue;
                const hours = slots.length * 0.5;
                const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                const dateLabel = date.toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric'
                });
                dayRows.push({ dateLabel, dayName, hours, slots: slots.join(', ') });
              }

              if (dayRows.length === 0) {
                return (
                  <tr key={rowIdx}>
                    <td>{row.staffName}</td>
                    <td>{row.monthLabel}</td>
                    <td className="sp-capitalize">{row.employmentType}</td>
                    <td colSpan="4" className="sp-fulltime-message">
                      No roster data for this month
                    </td>
                  </tr>
                );
              }

              return dayRows.map((day, dayIdx) => (
                <tr key={`${rowIdx}-${dayIdx}`}>
                  {dayIdx === 0 && (
                    <>
                      <td rowSpan={dayRows.length}>{row.staffName}</td>
                      <td rowSpan={dayRows.length}>{row.monthLabel}</td>
                      <td rowSpan={dayRows.length} className="sp-capitalize">{row.employmentType}</td>
                    </>
                  )}
                  <td>{day.dateLabel}</td>
                  <td>{day.dayName}</td>
                  <td>{day.hours}</td>
                  <td className="sp-slots-cell">{day.slots}</td>
                </tr>
              ));
            })}
          </tbody>
        </table>

        {/* Totals summary at bottom */}
        <div className="sp-detailed-totals">
          <h4>Pay Summary</h4>
          <table className="sp-report-table">
            <thead>
              <tr>
                <th>Staff Name</th>
                <th>Month</th>
                <th>Total Hours</th>
                <th>Rate</th>
                <th>Rate Type</th>
                <th>Currency</th>
                <th>Total Pay</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, idx) => (
                <tr key={idx} className={row.isFullTime ? 'sp-fulltime-row' : ''}>
                  <td>{row.staffName}</td>
                  <td>{row.monthLabel}</td>
                  <td>{row.isFullTime ? 'N/A' : row.totalMonthHours}</td>
                  <td>{row.rate || '-'}</td>
                  <td className="sp-capitalize">{row.rateType}</td>
                  <td>{row.currency}</td>
                  <td className="sp-pay-cell">{row.currency} {row.totalPay.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="staff-payroll-tab">
        <div className="sp-loading">
          <div className="sp-spinner"></div>
          <p>Loading payroll data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="staff-payroll-tab">
      {/* Header */}
      <div className="editor-page-header">
        <h2>Staff Payroll</h2>
        <div className="editor-page-actions">
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>Live Preview</button>
        </div>
      </div>
      <p className="sp-subtitle">Generate payroll reports for your staff members.</p>

      {/* Filters */}
      <div className="sp-filters">
        {/* Staff Multi-Select */}
        <div className="sp-filter-group" ref={staffDropdownRef}>
          <label>Staff</label>
          <div
            className="sp-multi-select"
            onClick={() => setStaffDropdownOpen(!staffDropdownOpen)}
          >
            <span className="sp-select-display">
              {selectedStaffIds.length === 0
                ? 'Select staff...'
                : `${selectedStaffIds.length} staff selected`}
            </span>
            <span className="sp-chevron">{staffDropdownOpen ? '\u25B2' : '\u25BC'}</span>
          </div>
          {staffDropdownOpen && (
            <div className="sp-dropdown-panel">
              <div className="sp-select-all" onClick={handleSelectAllStaff}>
                {selectedStaffIds.length === staffList.length ? 'Deselect All' : 'Select All'}
              </div>
              {staffList.map(s => {
                const id = s.staffId || s.id;
                return (
                  <label key={id} className="sp-dropdown-item">
                    <input
                      type="checkbox"
                      checked={selectedStaffIds.includes(id)}
                      onChange={() => toggleStaffSelection(id)}
                    />
                    {s.name}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Month Multi-Select */}
        <div className="sp-filter-group" ref={monthDropdownRef}>
          <label>Month(s)</label>
          <div
            className="sp-multi-select"
            onClick={() => setMonthDropdownOpen(!monthDropdownOpen)}
          >
            <span className="sp-select-display">
              {selectedMonths.length === 0
                ? 'Select months...'
                : `${selectedMonths.length} month(s) selected`}
            </span>
            <span className="sp-chevron">{monthDropdownOpen ? '\u25B2' : '\u25BC'}</span>
          </div>
          {monthDropdownOpen && (
            <div className="sp-dropdown-panel">
              <div className="sp-select-all" onClick={handleSelectAllMonths}>
                {selectedMonths.length === monthOptions.length ? 'Deselect All' : 'Select All'}
              </div>
              {monthOptions.map(opt => (
                <label key={opt.value} className="sp-dropdown-item">
                  <input
                    type="checkbox"
                    checked={selectedMonths.includes(opt.value)}
                    onChange={() => toggleMonthSelection(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Report Type Toggle */}
        <div className="sp-filter-group">
          <label>Report Type</label>
          <div className="sp-toggle">
            <button
              className={reportType === 'summary' ? 'active' : ''}
              onClick={() => setReportType('summary')}
            >
              Summary
            </button>
            <button
              className={reportType === 'detailed' ? 'active' : ''}
              onClick={() => setReportType('detailed')}
            >
              Detailed Breakdown
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="sp-actions">
        <button
          className="sp-generate-button"
          onClick={generateReport}
          disabled={selectedStaffIds.length === 0 || selectedMonths.length === 0 || generating}
        >
          {generating ? 'Generating...' : 'Generate Report'}
        </button>

        {reportData && reportData.length > 0 && (
          <button className="sp-export-button" onClick={handleExportCSV}>
            Export CSV
          </button>
        )}
      </div>

      {/* Report Output */}
      {reportData && (
        reportType === 'summary' ? renderSummaryReport() : renderDetailedReport()
      )}
    </div>
  );
}

export default StaffPayrollTab;
